/**
 * Clara360 Compliance Service
 * 
 * Purpose: Betreiberpflichten Management & CSDL Integration
 * Pattern: ensureValidSession → getCurrentUser → scoped query
 * RLS: Active - all queries filtered by created_by
 * 
 * @version 1.0.0
 * @date 2026-01-15
 */

import { masterSupabase as supabase } from '../lib/supabase-master.js';

class ComplianceService {
  constructor() {
    this.currentUser = null;
    
    // Cost estimation model (EUR per check/maintenance)
    this.costMap = {
      'elevator': 500,        // Aufzug: 500€/Prüfung
      'heating': 300,         // Heizung: 300€/Wartung
      'fire_safety': 400,     // Brandschutz: 400€/Prüfung
      'electrical': 200,      // Elektrik: 200€/Prüfung
      'other': 150            // Sonstiges: 150€/Prüfung
    };
  }

  // ============================================================================
  // AUTH HELPERS (copied from propertiesService.js pattern)
  // ============================================================================

  /**
   * Ensures valid session before API calls
   * @returns {Promise<Object>} Session object
   * @throws {Error} If session is invalid
   */
  async ensureValidSession() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.warn('⚠️ Session-Fehler:', error);
        throw new Error('Session konnte nicht abgerufen werden');
      }
      
      if (!session?.access_token) {
        console.warn('⚠️ Kein AccessToken - warte auf Refresh...');
        // Wait and retry
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const { data: { session: retrySession }, error: retryError } = await supabase.auth.getSession();
        
        if (retryError || !retrySession?.access_token) {
          throw new Error('Benutzer nicht authentifiziert - bitte neu anmelden');
        }
        
        console.log('✅ AccessToken nach Retry verfügbar');
        return retrySession;
      }
      
      console.log('✅ Gültige Session verfügbar');
      return session;
    } catch (error) {
      console.error('❌ ensureValidSession failed:', error);
      throw error;
    }
  }

  /**
   * Gets current authenticated user
   * @returns {Promise<Object>} User object
   * @throws {Error} If user is not authenticated
   */
  async getCurrentUser() {
    try {
      // Ensure session is valid
      await this.ensureValidSession();
      
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user) {
        throw new Error('Benutzer nicht authentifiziert');
      }
      this.currentUser = user;
      return user;
    } catch (error) {
      console.error('❌ Fehler bei User-Ermittlung:', error);
      throw error;
    }
  }

  // ============================================================================
  // CORE DATA ACCESS
  // ============================================================================

  /**
   * Loads compliance obligations for property
   * @param {string|null} propertyId - Property ID or null for all
   * @returns {Promise<Array>} Obligations array
   */
  async getObligations(propertyId = null) {
    try {
      console.log('🔍 Loading obligations for property:', propertyId);
      const user = await this.getCurrentUser();
      
      let query = supabase
        .from('compliance_obligations')
        .select(`
          *,
          property:properties!inner(id, property_name, address, city, created_by)
        `)
        .eq('is_active', true)
        .eq('property.created_by', user.id);
      
      if (propertyId && propertyId !== 'all') {
        query = query.eq('property_id', propertyId);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        console.error('❌ Error loading obligations:', error);
        throw error;
      }
      
      console.log(`✅ Loaded ${data?.length || 0} obligations`);
      return data || [];
    } catch (error) {
      console.error('❌ getObligations failed:', error);
      throw error;
    }
  }

  /**
   * Loads compliance deadlines for property with enriched data
   * @param {string|null} propertyId - Property ID or null for all
   * @returns {Promise<Array>} Deadlines array with status and days_until_due
   */
  async getDeadlines(propertyId = null) {
    try {
      console.log('🔍 Loading deadlines for property:', propertyId);
      const user = await this.getCurrentUser();
      
      // Load all deadlines with obligation details
      const { data, error } = await supabase
        .from('compliance_deadlines')
        .select(`
          *,
          obligation:compliance_obligations!inner(
            id,
            title,
            description,
            category,
            property_id,
            asset_name,
            interval_months,
            property:properties!inner(id, property_name, address, city, created_by)
          )
        `)
        .eq('obligation.property.created_by', user.id)
        .order('due_date', { ascending: true });
      
      if (error) {
        console.error('❌ Error loading deadlines:', error);
        throw error;
      }
      
      // Filter by property if specified
      let filteredData = data || [];
      if (propertyId && propertyId !== 'all') {
        filteredData = filteredData.filter(d => 
          d.obligation?.property_id === propertyId
        );
      }
      
      // Enrich with calculated fields
      const enrichedData = filteredData.map(deadline => ({
        ...deadline,
        status: this.calculateDeadlineStatus(deadline.due_date),
        days_until_due: this.calculateDaysUntilDue(deadline.due_date),
        property_name: deadline.obligation?.property?.property_name || 'Unknown',
        obligation_title: deadline.obligation?.title || 'Unknown',
        obligation_category: deadline.obligation?.category || 'other'
      }));
      
      console.log(`✅ Loaded ${enrichedData.length} deadlines`);
      return enrichedData;
    } catch (error) {
      console.error('❌ getDeadlines failed:', error);
      throw error;
    }
  }

  // ============================================================================
  // CALCULATION HELPERS
  // ============================================================================

  /**
   * Calculates deadline status based on due date
   * @param {string} dueDate - ISO Date String
   * @returns {string} 'OVERDUE' | 'DUE_SOON' | 'UPCOMING'
   */
  calculateDeadlineStatus(dueDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    
    const daysUntil = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) return 'OVERDUE';
    if (daysUntil <= 30) return 'DUE_SOON';
    return 'UPCOMING';
  }

  /**
   * Calculates days until due (negative = overdue)
   * @param {string} dueDate - ISO Date String
   * @returns {number} Days (negative = overdue)
   */
  calculateDaysUntilDue(dueDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    
    return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  }

  /**
   * Calculates compliance score (0-100) based on deadlines
   * Scoring: Overdue -10 points, DueSoon -5 points
   * @param {Array} deadlines - Array of deadlines
   * @returns {number} Score 0-100
   */
  calculateComplianceScore(deadlines) {
    if (!deadlines || deadlines.length === 0) {
      // No obligations = perfect score (capped for safety)
      return 100;
    }
    
    const overdue = deadlines.filter(d => d.status === 'OVERDUE').length;
    const dueSoon = deadlines.filter(d => d.status === 'DUE_SOON').length;
    const total = deadlines.length;
    
    // Penalty-based scoring
    const penalty = (overdue * 10) + (dueSoon * 5);
    const score = Math.max(0, 100 - penalty);
    
    console.log(`📊 Compliance Score: ${score} (${overdue} overdue, ${dueSoon} due soon, ${total} total)`);
    return score;
  }

  /**
   * Estimates annual compliance costs based on obligations
   * @param {Array} obligations - Array of obligations
   * @returns {number} Estimated annual costs in EUR
   */
  calculateAnnualCosts(obligations) {
    if (!obligations || obligations.length === 0) {
      return 0;
    }
    
    const totalCost = obligations.reduce((sum, obl) => {
      const baseCost = this.costMap[obl.category] || 150;
      const frequency = 12 / obl.interval_months;  // How often per year?
      const annualCost = baseCost * frequency;
      
      console.log(`  - ${obl.title}: ${baseCost}€ × ${frequency.toFixed(2)}/Jahr = ${annualCost.toFixed(0)}€`);
      return sum + annualCost;
    }, 0);
    
    console.log(`💰 Total Annual Costs: ${totalCost.toFixed(0)}€`);
    return Math.round(totalCost);
  }

  /**
   * Calculates liability flag (traffic light)
   * @param {Array} deadlines - Array of deadlines
   * @returns {string} 'high' | 'medium' | 'low'
   */
  calculateLiabilityFlag(deadlines) {
    if (!deadlines || deadlines.length === 0) {
      return 'low';
    }
    
    const overdue = deadlines.filter(d => d.status === 'OVERDUE');
    const critical = overdue.filter(d => 
      ['elevator', 'fire_safety'].includes(d.obligation_category)
    );
    
    if (critical.length > 0) {
      console.log(`🔴 Liability: HIGH (${critical.length} critical overdue)`);
      return 'high';
    }
    if (overdue.length > 0) {
      console.log(`🟡 Liability: MEDIUM (${overdue.length} overdue)`);
      return 'medium';
    }
    
    console.log('🟢 Liability: LOW');
    return 'low';
  }

  // ============================================================================
  // CSDL INTEGRATION (Main API for CSDL)
  // ============================================================================

  /**
   * Gets comprehensive compliance metrics for CSDL
   * This is the main entry point for CSDL integration
   * @param {string} propertyId - Property ID
   * @returns {Promise<Object>} Compliance metrics object
   */
  async getComplianceMetrics(propertyId) {
    try {
      console.log('📊 Calculating compliance metrics for property:', propertyId);
      
      // Load data
      const obligations = await this.getObligations(propertyId);
      const deadlines = await this.getDeadlines(propertyId);
      
      // Calculate metrics
      const metrics = {
        // Core metrics for CSDL
        annual_compliance_costs: this.calculateAnnualCosts(obligations),
        compliance_score: this.calculateComplianceScore(deadlines),
        liability_flag: this.calculateLiabilityFlag(deadlines),
        
        // Count metrics
        overdue_count: deadlines.filter(d => d.status === 'OVERDUE').length,
        due_soon_count: deadlines.filter(d => d.status === 'DUE_SOON').length,
        upcoming_count: deadlines.filter(d => d.status === 'UPCOMING').length,
        total_obligations: obligations.length,
        total_deadlines: deadlines.length,
        
        // Metadata
        property_id: propertyId,
        calculated_at: new Date().toISOString()
      };
      
      console.log('✅ Compliance metrics calculated:', metrics);
      return metrics;
    } catch (error) {
      console.error('❌ getComplianceMetrics failed:', error);
      
      // Fallback: Return safe defaults instead of throwing
      console.warn('⚠️ Returning fallback metrics due to error');
      return {
        annual_compliance_costs: 0,
        compliance_score: 100,  // Capped for safety
        liability_flag: 'low',
        overdue_count: 0,
        due_soon_count: 0,
        upcoming_count: 0,
        total_obligations: 0,
        total_deadlines: 0,
        property_id: propertyId,
        calculated_at: new Date().toISOString(),
        error: error.message
      };
    }
  }

  /**
   * Gets compliance summary for UI display
   * @param {string} propertyId - Property ID
   * @returns {Promise<Object>} Summary object
   */
  async getComplianceSummary(propertyId) {
    try {
      const metrics = await this.getComplianceMetrics(propertyId);
      const deadlines = await this.getDeadlines(propertyId);
      
      return {
        ...metrics,
        deadlines: deadlines.slice(0, 10),  // Top 10 upcoming
        status_emoji: metrics.liability_flag === 'high' ? '🔴' : 
                      metrics.liability_flag === 'medium' ? '🟡' : '🟢',
        status_text: metrics.liability_flag === 'high' ? 'Hohes Risiko' :
                     metrics.liability_flag === 'medium' ? 'Mittleres Risiko' : 'Geringes Risiko'
      };
    } catch (error) {
      console.error('❌ getComplianceSummary failed:', error);
      throw error;
    }
  }

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  /**
   * Creates new compliance obligation
   * @param {Object} obligationData - Obligation data
   * @returns {Promise<Object>} Created obligation
   */
  async createObligation(obligationData) {
    try {
      console.log('➕ Creating obligation:', obligationData.title);
      const user = await this.getCurrentUser();
      
      const { data, error } = await supabase
        .from('compliance_obligations')
        .insert({
          ...obligationData,
          created_by: user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (error) {
        console.error('❌ Error creating obligation:', error);
        throw error;
      }
      
      console.log('✅ Obligation created:', data.id);
      
      // Create audit log
      await this.createAuditLog(data.id, 'created', {
        title: data.title,
        category: data.category
      });
      
      return data;
    } catch (error) {
      console.error('❌ createObligation failed:', error);
      throw error;
    }
  }

  /**
   * Marks deadline as completed
   * @param {string} deadlineId - Deadline ID
   * @param {string|null} proofUrl - URL to proof document
   * @returns {Promise<Object>} Updated deadline
   */
  async markDeadlineCompleted(deadlineId, proofUrl = null) {
    try {
      console.log('✅ Marking deadline completed:', deadlineId);
      
      const { data, error } = await supabase
        .from('compliance_deadlines')
        .update({
          completed_at: new Date().toISOString(),
          proof_url: proofUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', deadlineId)
        .select()
        .single();
      
      if (error) {
        console.error('❌ Error marking deadline completed:', error);
        throw error;
      }
      
      console.log('✅ Deadline marked completed');
      
      // Create audit log
      await this.createAuditLog(data.obligation_id, 'completed', {
        deadline_id: deadlineId,
        proof_url: proofUrl,
        completed_at: data.completed_at
      });
      
      return data;
    } catch (error) {
      console.error('❌ markDeadlineCompleted failed:', error);
      throw error;
    }
  }

  /**
   * Creates audit log entry
   * @param {string} obligationId - Obligation ID
   * @param {string} action - Action type
   * @param {Object} metadata - Additional metadata
   */
  async createAuditLog(obligationId, action, metadata = {}) {
    try {
      const user = await this.getCurrentUser();
      
      await supabase
        .from('compliance_audit_log')
        .insert({
          obligation_id: obligationId,
          action,
          performed_by: user.id,
          metadata,
          created_at: new Date().toISOString()
        });
      
      console.log(`📝 Audit log created: ${action}`);
    } catch (error) {
      console.error('⚠️ Error creating audit log:', error);
      // Don't throw - audit log is not critical
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Gets annual cost estimate for property (public API)
   * @param {string} propertyId - Property ID
   * @returns {Promise<number>} Annual costs in EUR
   */
  async getAnnualCostEstimate(propertyId) {
    try {
      const obligations = await this.getObligations(propertyId);
      return this.calculateAnnualCosts(obligations);
    } catch (error) {
      console.error('❌ getAnnualCostEstimate failed:', error);
      return 0;  // Fallback
    }
  }

  /**
   * Gets compliance score for property (public API)
   * @param {string} propertyId - Property ID
   * @returns {Promise<number>} Score 0-100
   */
  async getComplianceScore(propertyId) {
    try {
      const deadlines = await this.getDeadlines(propertyId);
      return this.calculateComplianceScore(deadlines);
    } catch (error) {
      console.error('❌ getComplianceScore failed:', error);
      return 100;  // Fallback (capped for safety)
    }
  }
}

// Singleton instance
const complianceService = new ComplianceService();
export default complianceService;
